import * as yup from "yup";
import { useFormik } from "formik";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __orderInfoTmobilej } from "../../../../api/sim";

const TmobilejOrderInfoForm = () => {
  const [rawData, setRawData] = useState("");
  const [orderIds, setOrderIds] = useState([]);
  const [formData, setFormData] = useState(null);
  const { setAlertMessage } = useAlert();
  const [response, call, loading] = useAxios({ ...__orderInfoTmobilej });
  const nav = useNavigate();

  const lineValidationSchema = yup.object({
    order_id: yup.string().min(1).max(128).required(),
  });

  const parseOrderIds = (data) => {
    if (!data) return [];
    const ids = [];
    data
      .split(/\r?\n/)
      .map((line) => line.split(","))
      .flat()
      .map((v) => v.trim())
      .filter(Boolean)
      .forEach((id) => ids.push(id));
    return ids;
  };

  const validateFunction = (data) => {
    const errors = {};
    const ids = [];
    const seen = new Set();

    const parsed = parseOrderIds(data);
    if (!parsed.length) {
      errors.lines =
        "Please enter at least one Order ID (comma or newline separated).";
      setOrderIds([]);
      return errors;
    }

    for (let idx = 0; idx < parsed.length; idx += 1) {
      try {
        lineValidationSchema.validateSync({ order_id: parsed[idx] });
        if (seen.has(parsed[idx])) {
          errors.lines = `Duplicate Order ID at line ${idx + 1}`;
          break;
        }
        seen.add(parsed[idx]);
        ids.push(parsed[idx]);
      } catch (err) {
        errors.lines = `Invalid Order ID at line ${idx + 1}`;
        break;
      }
    }

    if (!errors.lines) {
      setOrderIds(ids);
    } else {
      setOrderIds([]);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {
      formik.setErrors(validateFunction(rawData));
      setFormData({});
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();
    reader.onload = ({ target }) => {
      setRawData(target.result);
      formik.setErrors(validateFunction(target.result));
    };
    reader.readAsText(inputFile);
  };

  const handleFileChange = (e) => {
    if (!e.target.files.length) return;
    const file = e.target.files[0];
    if (!file.name.endsWith(".csv")) {
      formik.setErrors({ ...formik.errors, upload: "Please input a csv file" });
      return;
    }
    handleParse(file);
  };

  useEffect(() => {
    if (formData && orderIds.length > 0) {
      call({
        data: {
          order_ids: [...orderIds],
        },
      });
    }
  }, [formData, orderIds]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({
        type: "success",
        message: "Order info fetch queued",
      });
      setOrderIds([]);
      setRawData("");
      nav("/tmobilej/order_info");
    }
    formik.setSubmitting(false);
  }, [response, nav, setAlertMessage, formik]);

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Fetch Order infos"
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.upload) ? true : false}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ marginRight: "1rem" }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(formik.errors.upload) ? (
                <FormHelperText id="upload-error-text">
                  {formik.errors.upload}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="lines">
              {"Add in the format"}
              <br></br>
              {"Order ID"}
              <br></br>
              {"Order ID"}
              <br></br>
            </InputLabel>
            <FormControl
              error={Boolean(formik.errors.lines) ? true : false}
              sx={{ width: "100%" }}
            >
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder={`Add in the format\nOrder ID\nOrder ID`}
                style={{ minWidth: 550 }}
                minRows={5}
                name="lines"
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  formik.setErrors(validateFunction(data));
                }}
              />
              {Boolean(formik.errors.lines) ? (
                <FormHelperText id="lines-error-text">
                  {formik.errors.lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={formik.errors.lines}
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Get Order Infos"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default TmobilejOrderInfoForm;
